import { jwtDecode } from 'jwt-decode';
import {
  UserSession,
  AuthTokens,
  DeviceCodeResponse,
  Auth0DeviceCodeError,
  DeviceCodeExchangeResult,
  DeviceCodeErrorResponse,
} from './../authTypes';
import { TokenManager } from './tokenManager';

interface AccessTokenClaims {
  sub?: string;
}

export interface UserInfo {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
}

export interface UserInfoClient {
  getUser(
    userId: string,
    accessToken: string,
    signal?: AbortSignal,
  ): Promise<UserInfo>;
}

// Abstract storage interfaces for dependency injection
export interface AuthStorage {
  saveTokens(tokens: AuthTokens): Promise<void>;
  getTokens(): Promise<AuthTokens | null>;
  saveSession(session: UserSession): Promise<void>;
  getSession(): Promise<UserSession | null>;
  clearAll(): Promise<void>;
  // Security utilities
  isTokenExpired(expiresAt: number): boolean;
}

export interface ApiClient {
  get<T>(
    url: string,
    params?: Record<string, string | number | boolean>,
    headers?: Record<string, string>,
    signal?: AbortSignal,
  ): Promise<T>;

  post<T>(
    url: string,
    data?: Record<string, unknown>,
    headers?: Record<string, string>,
    signal?: AbortSignal,
  ): Promise<T>;

  put<T>(
    url: string,
    data?: Record<string, unknown>,
    headers?: Record<string, string>,
    signal?: AbortSignal,
  ): Promise<T>;

  delete<T>(
    url: string,
    params?: Record<string, string | number | boolean>,
    headers?: Record<string, string>,
    signal?: AbortSignal,
  ): Promise<T>;
}

// Auth configuration interface
export interface AuthConfig {
  clientId: string;
  audience: string;
  domain: string;
  issuer: string;
  // Derive token and device code URLs from the issuer
  getTokenUrl(): string;
  getDeviceCodeUrl(): string;
}

// Define types for event handlers
export type AuthEventHandler = (session: UserSession) => void;
export type AuthEventType =
  | 'login'
  | 'logout'
  | 'session-updated'
  | 'token-refreshed';

export class AuthService {
  private static instance: AuthService | null = null;
  private session: UserSession | null = null;
  private tokenManager = new TokenManager();
  private eventHandlers: Map<AuthEventType, Set<AuthEventHandler>> = new Map();
  private authStorage: AuthStorage;
  private authConfig: AuthConfig;
  private apiClient: ApiClient;
  private userInfoClient: UserInfoClient;

  // For backward compatibility
  public onLoginSuccess: ((session: UserSession) => void) | null = null;

  private constructor(
    authStorage: AuthStorage,
    authConfig: AuthConfig,
    apiClient: ApiClient,
    userInfoClient: UserInfoClient,
  ) {
    this.authStorage = authStorage;
    this.authConfig = authConfig;
    this.apiClient = apiClient;
    this.userInfoClient = userInfoClient;
  }

  static getInstance(
    authStorage?: AuthStorage,
    authConfig?: AuthConfig,
    apiClient?: ApiClient,
    userInfoClient?: UserInfoClient,
  ): AuthService {
    if (!AuthService.instance) {
      if (!authStorage || !authConfig || !apiClient || !userInfoClient) {
        throw new Error(
          'AuthService requires authStorage, authConfig, apiClient, userInfoClient implementations on first initialization',
        );
      }
      AuthService.instance = new AuthService(
        authStorage,
        authConfig,
        apiClient,
        userInfoClient,
      );
    }
    return AuthService.instance;
  }

  /**
   * Register an event handler
   * @param eventType The event type to listen for
   * @param handler The function to call when the event occurs
   */
  addEventListener(eventType: AuthEventType, handler: AuthEventHandler): void {
    if (!this.eventHandlers.has(eventType)) {
      this.eventHandlers.set(eventType, new Set());
    }
    this.eventHandlers.get(eventType)?.add(handler);
  }

  /**
   * Remove an event handler
   * @param eventType The event type
   * @param handler The handler to remove
   */
  removeEventListener(
    eventType: AuthEventType,
    handler: AuthEventHandler,
  ): void {
    this.eventHandlers.get(eventType)?.delete(handler);
  }

  /**
   * Emit an event to all registered handlers
   * @param eventType The event type to emit
   * @param session The current user session
   */
  private emitEvent(eventType: AuthEventType, session: UserSession): void {
    // For backward compatibility
    if (eventType === 'login' && this.onLoginSuccess) {
      this.onLoginSuccess(session);
    }

    // Call all registered event handlers
    this.eventHandlers.get(eventType)?.forEach(handler => {
      try {
        handler(session);
      } catch (e) {
        console.error(`Error in ${eventType} event handler:`, e);
      }
    });
  }

  /**
   * Get the current authentication headers
   * @returns Headers object with auth token
   */
  getAuthHeaders(): Record<string, string> {
    if (!this.session) return {};

    return {
      Authorization: this.session.tokens.access_token,
    };
  }

  // Device code authentication using HTTP calls (like CDS app)
  async initiateDeviceCodeFlow(
    signal?: AbortSignal,
  ): Promise<DeviceCodeResponse> {
    try {
      const data = await this.apiClient.post<DeviceCodeResponse>(
        this.authConfig.getDeviceCodeUrl(),
        {
          client_id: this.authConfig.clientId,
          audience: this.authConfig.audience,
          scope: 'openid profile email offline_access',
        },
        {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        signal,
      );

      console.info('AuthService: Device code flow initiated successfully');

      return {
        device_code: data.device_code,
        user_code: data.user_code,
        verification_uri: data.verification_uri,
        verification_uri_complete: data.verification_uri_complete,
        expires_in: data.expires_in,
        interval: data.interval,
      };
    } catch (error) {
      // Don't log AbortError - it's intentional cancellation
      if (error instanceof Error && error.name === 'AbortError') {
        console.info('AuthService: Device code flow request was aborted');
        throw error; // Re-throw so hook can handle it
      }

      console.error('AuthService: Failed to initiate device code flow', error);
      throw error; // Throw the error so hook can handle it properly
    }
  }

  async exchangeDeviceCode(
    deviceCode: string,
    signal?: AbortSignal,
  ): Promise<DeviceCodeExchangeResult> {
    try {
      const data = await this.apiClient.post<
        AuthTokens | DeviceCodeErrorResponse
      >(
        this.authConfig.getTokenUrl(),
        {
          grant_type: 'urn:ietf:params:oauth:grant-type:device_code',
          device_code: deviceCode,
          client_id: this.authConfig.clientId,
        },
        {
          'Content-Type': 'application/json',
        },
        signal,
      );

      // Handle Auth0 device code errors
      if ('error' in data) {
        const errorResponse: DeviceCodeErrorResponse = {
          error: data.error as Auth0DeviceCodeError,
          error_description: data.error_description,
        };

        // Return error response - let the hook decide how to handle each error type
        return errorResponse;
      }

      const tokens: AuthTokens = {
        access_token: data.access_token,
        refresh_token: data.refresh_token,
        token_type: data.token_type,
        expires_in: data.expires_in,
        expires_at: Date.now() + data.expires_in * 1000,
        scope: data.scope,
      };
      return tokens;
    } catch (error: unknown) {
      if ((error as { name: string }).name === 'AbortError') {
        console.info('AuthService: Device code exchange aborted');
        return null; // Continue polling
      }
      const httpError = error as { message?: string; status?: number };
      if (httpError.status === 403) {
        return null; // Continue polling
      }

      throw error;
    }
  }

  // Token management using HTTP calls
  async refreshTokens(signal?: AbortSignal): Promise<AuthTokens> {
    try {
      const tokens = await this.authStorage.getTokens();
      if (!tokens?.refresh_token) {
        throw new Error('No refresh token available');
      }

      const data = await this.apiClient.post<AuthTokens>(
        this.authConfig.getTokenUrl(),
        {
          grant_type: 'refresh_token',
          refresh_token: tokens.refresh_token,
          client_id: this.authConfig.clientId,
        },
        {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        signal,
      );

      const newTokens: AuthTokens = {
        access_token: data.access_token,
        refresh_token: data.refresh_token || tokens.refresh_token, // Keep old refresh token if not provided
        token_type: data.token_type,
        expires_in: data.expires_in,
        expires_at: Date.now() + data.expires_in * 1000,
        scope: data.scope,
      };

      // Update session with new tokens
      if (this.session) {
        this.session.tokens = newTokens;
        await this.saveSession(this.session);

        // Emit token refresh event
        this.emitEvent('token-refreshed', this.session);
      }

      return newTokens;
    } catch (err: unknown) {
      console.error('AuthService: Failed to refresh tokens', err);
      throw new Error(
        `Token refresh failed: ${
          err instanceof Error ? err.message : String(err)
        }`,
      );
    }
  }

  async validateTokens(): Promise<boolean> {
    // First check if we have tokens in the current session
    if (this.session?.tokens) {
      const tokens = this.session.tokens;
      if (this.authStorage.isTokenExpired(tokens.expires_at)) {
        try {
          await this.refreshTokens();
          return true;
        } catch {
          return false;
        }
      }
      return true;
    }

    // Fallback to stored tokens
    const tokens = await this.authStorage.getTokens();
    if (!tokens) {
      return false;
    }

    if (this.authStorage.isTokenExpired(tokens.expires_at)) {
      try {
        await this.refreshTokens();
        return true;
      } catch {
        return false;
      }
    }

    return true;
  }

  // Session management
  async createSession(
    tokens: AuthTokens,
    signal: AbortSignal,
  ): Promise<UserSession> {
    try {
      const claims = jwtDecode<AccessTokenClaims>(tokens.access_token);
      if (!claims.sub) {
        throw new Error('Access token is missing the `sub` claim');
      }

      // Bail before hitting the network or persisting state so a cancel
      // can't leave the app in AUTHENTICATED with a partial session.
      if (signal.aborted) {
        const abortError = new Error('Session creation aborted');
        abortError.name = 'AbortError';
        throw abortError;
      }

      const userInfo = await this.userInfoClient.getUser(
        claims.sub,
        tokens.access_token,
        signal,
      );

      // Signal may have flipped while getUser was awaiting.
      if (signal.aborted) {
        const abortError = new Error('Session creation aborted');
        abortError.name = 'AbortError';
        throw abortError;
      }

      const fullName = [userInfo.firstName, userInfo.lastName]
        .filter(Boolean)
        .join(' ')
        .trim();

      const session: UserSession = {
        user: {
          id: userInfo.id,
          email: userInfo.email,
          name: fullName || userInfo.email,
        },
        tokens,
        expiresAt: tokens.expires_at,
      };

      await this.saveSession(session);
      this.session = session;

      // Start auto refresh
      this.tokenManager.startAutoRefresh(tokens, async () => {
        await this.refreshTokens();
      });

      // Emit login event for listeners
      this.emitEvent('login', session);

      return session;
    } catch (error) {
      // The SSO SDK rewraps AbortError as FetchError, so trust the
      // signal over error.name to detect cancellation.
      if (
        signal.aborted ||
        (error instanceof Error && error.name === 'AbortError')
      ) {
        const abortError = new Error('Session creation aborted');
        abortError.name = 'AbortError';
        throw abortError;
      }
      console.error('AuthService: Failed to create session', error);
      throw new Error(
        `Session creation failed: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
      );
    }
  }

  async saveSession(session: UserSession): Promise<void> {
    await this.authStorage.saveTokens(session.tokens);
    await this.authStorage.saveSession(session);
    this.session = session;
  }

  async loadSession(): Promise<UserSession | null> {
    if (this.session) {
      return this.session;
    }

    const session = await this.authStorage.getSession();

    if (session) {
      this.session = session;

      // Restart auto refresh if session is valid
      if (await this.validateTokens()) {
        this.tokenManager.startAutoRefresh(session.tokens, async () => {
          await this.refreshTokens();
        });

        // Emit session-updated event
        this.emitEvent('session-updated', session);
      }
    }

    return session;
  }

  async clearSession(): Promise<void> {
    this.tokenManager.stopAutoRefresh();
    await this.authStorage.clearAll();
    this.session = null;
  }

  async isAuthenticated(): Promise<boolean> {
    const session = await this.loadSession();
    return !!session?.user;
  }

  // Logout
  async logout(): Promise<void> {
    // Store a copy of the session before clearing for the event
    const oldSession = this.session;

    // For Auth0 device code flow, we just clear local session
    // Auth0 handles token revocation on the server side
    await this.clearSession();

    // Emit logout event if there was a session
    if (oldSession) {
      this.emitEvent('logout', oldSession);
    }
  }

  // Get current session
  getCurrentSession(): UserSession | null {
    return this.session;
  }

  static resetInstance(): void {
    AuthService.instance = null;
  }
}

// Factory function for creating auth service with storage implementations
export const createAuthService = (
  authStorage: AuthStorage,
  authConfig: AuthConfig,
  apiClient: ApiClient,
  userInfoClient: UserInfoClient,
): AuthService => {
  return AuthService.getInstance(
    authStorage,
    authConfig,
    apiClient,
    userInfoClient,
  );
};

// Note: authService instance should be created by the consumer with their storage implementations
// Example: export const authService = createAuthService(new MyAuthStorage(), new MyAuthConfig(), new MyApiClient(), new MyUserInfoClient());
